import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Flame, TrendingDown, TrendingUp, Minus, Search, ChevronLeft, ChevronRight, Info, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, Radio } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

const PAGE_SIZE = 100;
const burnColor = (b) => (b >= 75 ? 'bg-red-500 text-white' : b >= 45 ? 'bg-amber-500 text-black' : 'bg-emerald-600 text-white');
const zoneColor = { green: 'bg-emerald-600 text-white', yellow: 'bg-amber-500 text-black', red: 'bg-red-500 text-white' };
const fmtDate = (d) => (d ? d.split('-').reverse().slice(0, 2).join('/') : '');
const fmtDelta = (d) => (d == null ? '-' : `${d > 0 ? '+' : ''}${d.toFixed(1)}%`);

const STATUS = {
  burning: { key: 'Burning', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
  highExposure: { key: 'HighExposure', cls: 'bg-orange-500/20 text-orange-300 border-orange-500/40' },
  increasing: { key: 'Increasing', cls: 'bg-sky-500/20 text-sky-300 border-sky-500/40' },
  stable: { key: 'Stable', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' },
  decreasing: { key: 'Decreasing', cls: 'bg-slate-500/20 text-slate-400 border-slate-500/40' },
  losing: { key: 'Losing', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
  neutral: { key: 'Neutral', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  holding: { key: 'Holding', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
};

const HIGH_EXPOSURE_WEEKS = 3;

const statusOf = (s, audience) => {
  if (audience) return s.burning ? 'burning' : s.zone === 'red' ? 'losing' : s.zone === 'yellow' ? 'neutral' : 'holding';
  if ((s.high_weeks || 0) >= HIGH_EXPOSURE_WEEKS) return 'highExposure';
  return { rising: 'increasing', falling: 'decreasing' }[s.trajectory] || 'stable';
};

const LOW_SAMPLE = 5;
const explain = (s, audience, week, t) => {
  const range = { from: fmtDate(week.week_start), to: fmtDate(week.week_end) };
  if (audience) {
    const d = s.delta || 0;
    return [t(d < 0 ? 'burnTipWeekLoss' : 'burnTipWeekGain', { ...range, d: Math.abs(d).toFixed(1) })];
  }
  const w = s.weekly_spins || [];
  const n = s.spins - (w.length > 1 ? w[w.length - 2] : 0);
  const diff = n > 0 ? t('burnTipMore', { n }) : n < 0 ? t('burnTipLess', { n: -n }) : t('burnTipSame');
  return [
    t('burnTipWeekSpins', { ...range, spins: s.spins, diff }),
    t('burnTipHighWeeks', { n: s.high_weeks || 0 }),
    t('burnTipStationChoice'),
  ];
};

const lowSample = (s, audience) => audience && s.measured_plays != null && s.measured_plays < LOW_SAMPLE;

const StatusBadge = ({ s, audience, week, t }) => {
  const [open, setOpen] = useState(false);
  const st = statusOf(s, audience);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`burn-status-${s.rank}`} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${STATUS[st].cls}`}>
          {st === 'burning' && <Flame size={12} />}{lowSample(s, audience) && <AlertTriangle size={11} className="text-amber-300" />}{t(`burnSt${STATUS[st].key}`)}<Info size={11} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" collisionPadding={12} className="w-72 sm:w-80 bg-slate-900 border-white/10 text-slate-200 text-sm" data-testid={`burn-tip-${s.rank}`}>
        <div className="font-semibold text-white mb-1">{s.artist} - {s.title}</div>
        <ul className="space-y-1 text-slate-300">{explain(s, audience, week, t).map((l) => <li key={l}>{l}</li>)}</ul>
        {lowSample(s, audience) && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-300" data-testid={`burn-low-sample-${s.rank}`}>
            <AlertTriangle size={12} />{t('burnTipLowSample', { n: s.measured_plays, total: s.spins })}
          </div>
        )}
        <div className="mt-2 pt-2 border-t border-white/10 text-amber-300">{t(`burnAdv${STATUS[st].key}`)}</div>
      </PopoverContent>
    </Popover>
  );
};

const Arrow = ({ dir }) => {
  if (dir === 'falling' || dir < 0) return <TrendingDown size={14} className="text-red-400" />;
  if (dir === 'rising' || dir > 0) return <TrendingUp size={14} className="text-emerald-400" />;
  return <Minus size={14} className="text-slate-400" />;
};

const Toggle = ({ view, setView }) => (
  <div className="flex rounded-lg overflow-hidden border border-amber-500/40 text-sm">
    {[['airplay', 'Airplay'], ['audience', 'Audience']].map(([k, label]) => (
      <button key={k} data-testid={`burn-view-${k}`} onClick={() => setView(k)}
        className={`px-3 py-1.5 transition-colors ${view === k ? 'bg-amber-500 text-black' : 'bg-transparent text-amber-300 hover:bg-amber-500/10'}`}>
        {label}
      </button>
    ))}
  </div>
);

const Pager = ({ page, pages, setPage, t }) => (
  <div className="flex items-center justify-end gap-2 mt-3 text-sm text-slate-300" data-testid="burn-pager">
    <button data-testid="burn-prev-page" disabled={page <= 1} onClick={() => setPage(page - 1)}
      className="p-1.5 rounded-md border border-white/10 disabled:opacity-30 hover:bg-white/10"><ChevronLeft size={16} /></button>
    <span data-testid="burn-page-info">{t('burnPage', { p: page, n: Math.max(pages, 1) })}</span>
    <button data-testid="burn-next-page" disabled={page >= pages} onClick={() => setPage(page + 1)}
      className="p-1.5 rounded-md border border-white/10 disabled:opacity-30 hover:bg-white/10"><ChevronRight size={16} /></button>
  </div>
);

const Row = ({ s, audience, week, t }) => (
  <tr className="border-t border-white/5 hover:bg-white/5" data-testid={`burn-row-${s.rank}`}>
    <td className="px-3 py-2 text-slate-400">{s.rank}</td>
    <td className="px-3 py-2 font-medium text-white">{s.artist}</td>
    <td className="px-3 py-2 text-slate-200">{s.title}</td>
    <td className="px-3 py-2 text-center text-slate-400">{s.release_year || '-'}</td>
    <td className="px-3 py-2 text-center text-slate-200">{s.spins}</td>
    <td className="px-3 py-2 text-center text-slate-400 whitespace-nowrap">{(s.weekly_spins || []).join(' · ')}</td>
    <td className="px-3 py-2 text-center">
      {audience
        ? <span className={`inline-block min-w-[3.5rem] rounded-md px-2 py-0.5 font-semibold ${zoneColor[s.zone] || ''}`} data-testid={`burn-delta-${s.rank}`}>{fmtDelta(s.delta)}</span>
        : <span className={`inline-block min-w-[2.2rem] rounded-md px-2 py-0.5 font-semibold ${burnColor(s.burn)}`} data-testid={`burn-score-${s.rank}`}>{s.burn}</span>}
    </td>
    <td className="px-3 py-2 text-center"><div className="flex justify-center"><Arrow dir={audience ? s.delta_trend : s.trajectory} /></div></td>
    <td className="px-3 py-2 text-center"><StatusBadge s={s} audience={audience} week={week} t={t} /></td>
  </tr>
);

const COLUMNS = [
  ['rank', '#', 'left'], ['artist', 'burnColArtist', 'left'], ['title', 'burnColTitle', 'left'],
  ['year', 'burnColYear', 'center'], ['spins', 'burnColSpins', 'center'], ['weeks', 'burnCol4w', 'center'],
  ['score', null, 'center'], ['trend', 'burnColTrend', 'center'], ['status', 'burnColStatus', 'center'],
];
const TEXT_COLS = ['artist', 'title'];

const SortHeader = ({ col, label, align, sort, setSort }) => {
  const active = sort.field === col;
  const Icon = !active ? ArrowUpDown : sort.dir === 'desc' ? ArrowDown : ArrowUp;
  const next = () => setSort(active
    ? { field: col, dir: sort.dir === 'desc' ? 'asc' : 'desc' }
    : { field: col, dir: TEXT_COLS.includes(col) || col === 'rank' ? 'asc' : 'desc' });
  return (
    <th className={`px-3 py-2 text-${align}`}>
      <button data-testid={`burn-sort-${col}`} onClick={next}
        className={`inline-flex items-center gap-1 transition-colors hover:text-amber-300 ${active ? 'text-amber-300' : ''}`}>
        {label}<Icon size={12} className={active ? '' : 'opacity-40'} />
      </button>
    </th>
  );
};

const Table = ({ songs, audience, week, sort, setSort, t }) => (
  <div className="overflow-x-auto rounded-xl border border-white/10">
    <table className="w-full text-sm" data-testid="burn-table">
      <thead className="bg-slate-900/80 text-slate-300">
        <tr>
          {COLUMNS.map(([col, key, align]) => (
            <SortHeader key={col} col={col} align={align} sort={sort} setSort={setSort}
              label={col === 'rank' ? '#' : t(key || (audience ? 'burnColDelta' : 'burnColBurn'))} />
          ))}
        </tr>
      </thead>
      <tbody>{songs.map((s) => <Row key={s.key} s={s} audience={audience} week={week} t={t} />)}</tbody>
    </table>
  </div>
);

export const BurnTab = ({ stationId }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [view, setView] = useState('airplay');
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ field: 'spins', dir: 'desc' });
  const backendUrl = getBackendUrl();

  useEffect(() => { const h = setTimeout(() => { setQuery(q); setPage(1); }, 300); return () => clearTimeout(h); }, [q]);
  useEffect(() => { setPage(1); }, [view, stationId, sort]);

  useEffect(() => {
    if (!stationId) return;
    setLoading(true);
    setError(false);
    axios
      .get(`${backendUrl}/api/songs-analyzer/burn/${stationId}`, {
        params: { view, q: query, page, page_size: PAGE_SIZE, sort: sort.field, direction: sort.dir },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [stationId, view, query, page, sort, backendUrl]);

  const audience = view === 'audience';
  const songs = data?.songs || [];
  const ready = !loading && !error;

  return (
    <div data-testid="burn-tab-content">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-amber-300">
          <Flame size={18} />
          <span className="font-semibold">Burn</span>
          {data?.week_start && (
            <span className="text-xs text-slate-400" data-testid="burn-week-label">
              {t('burnWeek', { from: fmtDate(data.week_start), to: fmtDate(data.week_end), n: data.station_total })}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="burn-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('burnSearch')}
              className="w-40 sm:w-56 pl-7 pr-2 py-1.5 text-sm rounded-lg bg-slate-900/60 border border-white/10 text-white focus:outline-none focus:border-amber-500/60" />
          </div>
          <Toggle view={view} setView={setView} />
        </div>
      </div>

      {loading && <div className="p-8 text-center text-slate-400" data-testid="burn-loading">{t('burnLoading')}</div>}
      {!loading && error && <div className="p-8 text-center text-amber-300" data-testid="burn-error">{t('burnError')}</div>}
      {ready && data?.status === 'pending' && <div className="p-8 text-center text-slate-400" data-testid="burn-pending">{t('burnPending')}</div>}
      {ready && data?.status === 'ok' && !songs.length && (
        <div className="p-8 text-center text-slate-400" data-testid="burn-empty">{t(audience && !data.audience_reliable ? 'burnNoAudience' : 'burnEmpty')}</div>
      )}
      {ready && songs.length > 0 && (
        <>
          {audience && !data.audience_reliable && <div className="mb-2 text-xs text-amber-300" data-testid="burn-audience-warning">{t('burnAudienceWarn')}</div>}
          {!audience && (
            <div className="mb-2 flex items-center gap-1.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-1.5 text-xs text-sky-200" data-testid="burn-airplay-note">
              <Radio size={13} />{t('burnAirplayNote')}
            </div>
          )}
          <div className="mb-2 flex items-center gap-1.5 text-xs text-slate-400" data-testid="burn-tap-hint"><Info size={12} />{t('burnTapHint')} {t('burnSortHint')}</div>
          <Table songs={songs} audience={audience} week={data} sort={sort} setSort={setSort} t={t} />
          <Pager page={data.page} pages={data.pages} setPage={setPage} t={t} />
        </>
      )}
      <p className="mt-2 text-xs text-slate-500" data-testid="burn-legend">{t(audience ? 'burnLegendAudience' : 'burnLegendAirplay')}</p>
    </div>
  );
};

export default BurnTab;
